// Dequantization (decode side only). Encoder lives in C++ Lambda.
//
// Documented epsilons (max abs reconstruction error per scalar):
//   position:  range_axis / (2^bits - 1)
//   shDc:      2 / (2^bits - 1) clamped to [-SHDC_RANGE, SHDC_RANGE]
//   scale:     log_range / (2^bits - 1)
//   rotation:  ~ 1.4142 / (2^bits - 1) (smallest-three at 8-bit, direct at 16-bit)
//   opacity:   1 / (2^bits - 1) (sigmoid space)

export function dequantizePosition(
  q: Int32Array,
  blockAabbMin: [number, number, number],
  blockAabbMax: [number, number, number],
  bits: number,
): Float32Array {
  const max = (1 << bits) - 1;
  const n = q.length / 3;
  const out = new Float32Array(q.length);
  const sx = blockAabbMax[0] - blockAabbMin[0];
  const sy = blockAabbMax[1] - blockAabbMin[1];
  const sz = blockAabbMax[2] - blockAabbMin[2];
  for (let i = 0; i < n; i++) {
    out[i * 3] = blockAabbMin[0] + (max > 0 ? (q[i * 3]! / max) * sx : 0);
    out[i * 3 + 1] = blockAabbMin[1] + (max > 0 ? (q[i * 3 + 1]! / max) * sy : 0);
    out[i * 3 + 2] = blockAabbMin[2] + (max > 0 ? (q[i * 3 + 2]! / max) * sz : 0);
  }
  return out;
}

export const SHDC_RANGE = 4;

export function dequantizeShDc(q: Int32Array, bits: number): Float32Array {
  const max = (1 << bits) - 1;
  const out = new Float32Array(q.length);
  for (let i = 0; i < q.length; i++) {
    out[i] = max > 0 ? (q[i]! / max) * (2 * SHDC_RANGE) - SHDC_RANGE : -SHDC_RANGE;
  }
  return out;
}

const SCALE_MIN = -10;
const SCALE_MAX = 5;

export function dequantizeScale(q: Int32Array, bits: number): Float32Array {
  const max = (1 << bits) - 1;
  const out = new Float32Array(q.length);
  const range = SCALE_MAX - SCALE_MIN;
  for (let i = 0; i < q.length; i++) {
    out[i] = max > 0 ? SCALE_MIN + (q[i]! / max) * range : SCALE_MIN;
  }
  return out;
}

const SQRT_HALF = Math.SQRT1_2;

export function dequantizeRotation(q: Int32Array, bits: number): Float32Array {
  const n = q.length / 4;
  const out = new Float32Array(n * 4);
  if (bits >= 16) {
    const max = (1 << bits) - 1;
    for (let i = 0; i < n; i++) {
      const x = max > 0 ? (q[i * 4]! / max) * 2 - 1 : 0;
      const y = max > 0 ? (q[i * 4 + 1]! / max) * 2 - 1 : 0;
      const z = max > 0 ? (q[i * 4 + 2]! / max) * 2 - 1 : 0;
      const w = max > 0 ? (q[i * 4 + 3]! / max) * 2 - 1 : 0;
      const norm = Math.sqrt(x * x + y * y + z * z + w * w) || 1;
      out[i * 4] = x / norm;
      out[i * 4 + 1] = y / norm;
      out[i * 4 + 2] = z / norm;
      out[i * 4 + 3] = w / norm;
    }
  } else {
    const max = (1 << bits) - 1;
    for (let i = 0; i < n; i++) {
      const maxIdx = q[i * 4]! & 3;
      const others: number[] = [];
      let sumSq = 0;
      for (let s = 1; s <= 3; s++) {
        const v = max > 0 ? (q[i * 4 + s]! / max) * (2 * SQRT_HALF) - SQRT_HALF : 0;
        others.push(v);
        sumSq += v * v;
      }
      const largest = Math.sqrt(Math.max(0, 1 - sumSq));
      let oIdx = 0;
      for (let j = 0; j < 4; j++) {
        if (j === maxIdx) {
          out[i * 4 + j] = largest;
        } else {
          out[i * 4 + j] = others[oIdx++]!;
        }
      }
    }
  }
  return out;
}

export function dequantizeOpacity(q: Int32Array, bits: number): Float32Array {
  const max = (1 << bits) - 1;
  const out = new Float32Array(q.length);
  for (let i = 0; i < q.length; i++) {
    const a = max > 0 ? q[i]! / max : 0;
    out[i] = inverseSigmoid(a);
  }
  return out;
}

function clamp(v: number, lo: number, hi: number): number {
  if (v < lo) return lo;
  if (v > hi) return hi;
  return v;
}

function inverseSigmoid(p: number): number {
  const eps = 1e-7;
  const c = clamp(p, eps, 1 - eps);
  return Math.log(c / (1 - c));
}
